import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { BriefItemEditor } from "@/components/BriefItemEditor";
import { Markdown } from "@/components/Markdown";

/** The general ("whole day") and unassigned instruction blocks — addable and editable by hand. */
export const BriefItemGroup = ({
  title,
  hint,
  items,
  date,
  isGeneral,
  canWrite,
  onChange,
  testId,
}) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  if (!canWrite && !items.length) return null;

  const save = async (form, item) => {
    if (!form.instruction.trim()) return toast.error("Write the instruction first");
    const payload = {
      date,
      matter_id: item?.matter_id ?? null,
      event_id: item?.event_id ?? null,
      instruction: form.instruction.trim(),
      bare_acts: form.bare_acts?.trim() || null,
      judgments: form.judgments?.trim() || null,
      is_general: isGeneral,
    };
    try {
      if (item?.item_id) await api.put(`/brief/items/${item.item_id}`, payload);
      else await api.post("/brief/items", payload);
      setAdding(false);
      setEditing(null);
      toast.success(item?.item_id ? "Instruction updated" : "Instruction added");
      onChange();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/brief/items/${item.item_id}`);
      toast.success("Instruction removed");
      onChange();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <section className="surface p-5" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="label-caps">{title}</p>
          {hint ? <p className="mt-1.5 text-sm text-muted-foreground">{hint}</p> : null}
        </div>
        {canWrite && !adding ? (
          <button
            data-testid={`${testId}-add`}
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-sm border border-border hover:bg-accent transition-colors duration-200"
          >
            <Plus className="h-4 w-4" /> Instruction
          </button>
        ) : null}
      </div>

      <div className="mt-4 space-y-3">
        {items.map((item) =>
          editing === item.item_id ? (
            <BriefItemEditor
              key={item.item_id}
              item={item}
              testId={`${testId}-edit-${item.item_id}`}
              onSave={(form) => save(form, item)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div
              key={item.item_id}
              data-testid={`brief-${isGeneral ? "general" : "extra"}-${item.item_id}`}
              className="border border-border rounded-sm px-4 py-3 flex items-start gap-3"
            >
              <div className="min-w-0 flex-1">
                {!isGeneral ? (
                  <p className="text-sm font-semibold">{item.matter_title || "Unassigned"}</p>
                ) : null}
                <Markdown
                  text={item.instruction}
                  className={`text-base leading-relaxed${isGeneral ? "" : " mt-1"}`}
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  {item.bare_acts ? (
                    <span className="chip chip-session chip-wrap">{item.bare_acts}</span>
                  ) : null}
                  {item.judgments ? (
                    <span className="chip chip-post chip-wrap">{item.judgments}</span>
                  ) : null}
                  {item.source === "voice" ? (
                    <span className="chip chip-oos">from voice note</span>
                  ) : null}
                </div>
              </div>
              {canWrite ? (
                <div className="flex shrink-0 gap-1">
                  <button
                    data-testid={`${testId}-edit-btn-${item.item_id}`}
                    onClick={() => setEditing(item.item_id)}
                    className="h-9 w-9 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
                    aria-label="Edit instruction"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`${testId}-delete-btn-${item.item_id}`}
                    onClick={() => remove(item)}
                    className="h-9 w-9 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
                    aria-label="Delete instruction"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>
          ),
        )}

        {adding ? (
          <BriefItemEditor
            testId={`${testId}-new`}
            onSave={(form) => save(form, null)}
            onCancel={() => setAdding(false)}
          />
        ) : null}

        {!items.length && !adding ? (
          <p
            className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-3"
            data-testid={`${testId}-empty`}
          >
            Nothing here yet — add one, or dictate it with the day's note.
          </p>
        ) : null}
      </div>
    </section>
  );
};
